import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import { CLIENT_GUIDE } from "@/data/content";

export default function ClientGuide() {
  const g = CLIENT_GUIDE;
  return (
    <div className="pt-8 md:pt-12">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-sage mb-6">Client guide</p>
        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
          lines={["What to wear,", "how to prepare"]}
        />
        <Reveal delay={0.1}>
          <p className="mt-10 max-w-2xl text-inksoft text-sm md:text-lg leading-relaxed">
            {g.intro}
          </p>
        </Reveal>
      </section>

      {/* Guiding principles */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-24 md:mt-32">
        <Reveal>
          <p className="overline text-sage mb-6">Guiding principles</p>
          <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-tight max-w-2xl">
            Six quiet rules that always work
          </h2>
        </Reveal>
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
          {g.principles.map((p, i) => (
            <Reveal key={p.title} delay={(i % 2) * 0.05}>
              <div data-testid={`guide-principle-${i}`}>
                <p className="font-serif italic text-sage text-2xl mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-serif text-2xl md:text-3xl text-ink mb-4">
                  {p.title}
                </h3>
                <p className="text-inksoft text-base leading-relaxed">
                  {p.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Style board — a mood board pulled from real client work */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-28 md:mt-40">
        <Reveal>
          <p className="overline text-sage mb-6">Style board</p>
          <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-tight max-w-3xl">
            What those tips look like in real sessions
          </h2>
        </Reveal>
        <div className="mt-14 md:mt-20 columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
          {g.styleBoard.map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 0.05}>
              <Link
                to={`/portfolio?cat=${s.catKey}`}
                data-testid={`guide-styleboard-${i}`}
                className="mb-4 md:mb-6 break-inside-avoid group block"
              >
                <div className="overflow-hidden bg-stone2">
                  <img
                    src={s.src}
                    alt={s.caption}
                    loading="lazy"
                    className="block w-full h-auto transition-transform duration-700 ease-out will-change-transform group-hover:scale-[1.03]"
                  />
                </div>
                <div className="mt-3 flex items-baseline justify-between gap-4">
                  <p className="text-inksoft text-sm leading-relaxed italic font-serif group-hover:text-sage transition-colors">
                    {s.caption}
                  </p>
                  <p className="overline text-sage whitespace-nowrap">{s.cat} →</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* By session type */}
      <section className="bg-stone2 mt-28 md:mt-40 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal>
            <p className="overline text-sage mb-6">By session type</p>
            <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-3xl">
              Styling notes for your session
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {g.bySession.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div
                  data-testid={`guide-session-${i}`}
                  className="bg-cream p-8 md:p-10 h-full"
                >
                  <h3 className="font-serif text-2xl md:text-3xl text-ink mb-6">
                    {s.title}
                  </h3>
                  <ul className="space-y-4">
                    {s.tips.map((tip, j) => (
                      <li
                        key={j}
                        className="flex gap-3 text-inksoft text-sm leading-relaxed"
                      >
                        <Check
                          size={16}
                          className="text-sage flex-shrink-0 mt-1"
                          strokeWidth={2}
                        />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Day-of checklist */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="overline text-sage mb-6">The day of</p>
          <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-3xl">
            A tiny pre-flight checklist
          </h2>
        </Reveal>
        <div className="mt-14 md:mt-20 max-w-3xl">
          <ul className="space-y-6" data-testid="guide-dayof-list">
            {g.dayOf.map((line, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <li className="flex gap-4 items-start border-b border-ink/10 pb-6">
                  <span className="font-serif italic text-sage text-xl leading-none pt-1 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-ink text-lg md:text-xl leading-relaxed font-serif font-light">
                    {line}
                  </p>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-sagelight py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-xl">
            Still second-guessing your outfit?
          </h2>
          <Link
            to="/contact"
            data-testid="guide-contact-link"
            className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-9 py-4 text-sm font-medium hover:bg-sagedark transition-colors"
          >
            Send me a photo
            <ArrowUpRight
              size={17}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>
      </section>
    </div>
  );
}
